

import { useState, useEffect, useRef } from "react"
import PdfViewer from "../../pages/PdfViewer"
import ChatBox from "./s3-chat-box"
import { Camera, X, Maximize2, Minimize2, Download } from "lucide-react"

const PdfViewerWithChat = ({ file }) => {
  const [selectedScreenshot, setSelectedScreenshot] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [proxiedUrl, setProxiedUrl] = useState(null)
  const containerRef = useRef(null)
  const pdfPageRef = useRef(null)

  // Screenshot selection states
  const [isSelectingArea, setIsSelectingArea] = useState(false)
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 })
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const selectionRef = useRef(null)

  // Layout states
  const [chatExpanded, setChatExpanded] = useState(false)

  useEffect(() => {
    // Reset state when file changes
    setSelectedScreenshot(null)
    setIsLoading(true)
    setError(null)
    setIsSelectingArea(false)

    if (file && (file.includes("amazonaws.com") || file.includes("s3."))) {
      setProxiedUrl(`/api/proxy/papers?url=${encodeURIComponent(file)}`)
    } else {
      setProxiedUrl(file)
    }
  }, [file])

  useEffect(() => {
    if (!containerRef.current) return

    // Create a MutationObserver to detect when the PDF is loaded
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          const pdfPage = containerRef.current.querySelector(".react-pdf__Page")
          if (pdfPage) {
            pdfPageRef.current = pdfPage
            setIsLoading(false)
            observer.disconnect()
            break
          }
        }
      }
    })

    observer.observe(containerRef.current, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [proxiedUrl])

  const startScreenshotSelection = () => {
    setIsSelectingArea(true)
    setSelectionStart({ x: 0, y: 0 })
    setSelectionEnd({ x: 0, y: 0 })
    setIsDragging(false)
  }

  const cancelScreenshotSelection = () => {
    setIsSelectingArea(false)
  }

  const handleMouseDown = (e) => {
    if (!isSelectingArea) return

    const rect = containerRef.current.getBoundingClientRect()

    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setSelectionStart({ x, y })
    setSelectionEnd({ x, y })
    setIsDragging(true)
  }

  const handleMouseMove = (e) => {
    if (!isSelectingArea || !isDragging) return

    const rect = containerRef.current.getBoundingClientRect()

    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width)
    const y = Math.min(Math.max(0, e.clientY - rect.top), rect.height)

    setSelectionEnd({ x, y })
  }

  const handleMouseUp = () => {
    if (!isSelectingArea || !isDragging) return
    setIsDragging(false)

    const width = Math.abs(selectionEnd.x - selectionStart.x)
    const height = Math.abs(selectionEnd.y - selectionStart.y)

    if (width > 10 && height > 10) {
      captureSelectedArea()
    }
  }

  const captureSelectedArea = async () => {
    try {
      const html2canvasModule = await import("html2canvas")
      const html2canvas = html2canvasModule.default

      const pdfPage = pdfPageRef.current
      if (!pdfPage) {
        console.error("PDF page not found")
        return
      }

      const x = Math.min(selectionStart.x, selectionEnd.x)
      const y = Math.min(selectionStart.y, selectionEnd.y)
      const width = Math.abs(selectionEnd.x - selectionStart.x)
      const height = Math.abs(selectionEnd.y - selectionStart.y)


      const containerRect = containerRef.current.getBoundingClientRect()


      const pdfPageRect = pdfPage.getBoundingClientRect()

      const offsetX = pdfPageRect.left - containerRect.left
      const offsetY = pdfPageRect.top - containerRect.top

      const pdfX = x - offsetX
      const pdfY = y - offsetY



      const canvas = await html2canvas(pdfPage, {
        useCORS: true,
        logging: false,
        removeContainer: true,
        scale: window.devicePixelRatio, // Account for device pixel ratio
      })

      const scaleX = canvas.width / pdfPageRect.width
      const scaleY = canvas.height / pdfPageRect.height


      const scaledX = pdfX * scaleX
      const scaledY = pdfY * scaleY
      const scaledWidth = width * scaleX
      const scaledHeight = height * scaleY


      const croppedCanvas = document.createElement("canvas")
      croppedCanvas.width = scaledWidth
      croppedCanvas.height = scaledHeight
      const ctx = croppedCanvas.getContext("2d")

      ctx.drawImage(
        canvas,
        scaledX,
        scaledY,
        scaledWidth,
        scaledHeight, // Source rectangle
        0,
        0,
        scaledWidth,
        scaledHeight, // Destination rectangle
      )

      // Convert to data URL
      const screenshotData = croppedCanvas.toDataURL("image/png")
      setSelectedScreenshot(screenshotData)
      setIsSelectingArea(false)
    } catch (error) {
      console.error("Error capturing screenshot:", error)
      setIsSelectingArea(false)
    }
  }

  const handlePdfError = (err) => {
    setIsLoading(false)
    setError(err.message || "Failed to load PDF")
  }

  const toggleChatSize = () => {
    setChatExpanded(!chatExpanded)
  }

  return (
    <div className="flex h-full flex-col">
      {/* Content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left side - PDF Viewer with Screenshot Selection */}
        <div
          className={`border-r relative transition-all duration-300 ${chatExpanded ? "w-1/2" : "w-2/3"}`}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: isSelectingArea ? "crosshair" : "default" }}
        >
          {/* PDF Toolbar */}
          <div className="absolute top-2 left-[10vw] z-10 flex space-x-2">
            {!isSelectingArea ? (
              <>
                <button
                  onClick={startScreenshotSelection}
                  disabled={isLoading}
                  className="relative inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600 shadow-xl hover:scale-105 transition-transform duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                >
                  <Camera size={18} className="transition-transform duration-150 hover:rotate-12" />
                  <span>Capture to Ask in Chat</span>
                  {/* Animated overlay effect */}
                  <span className="absolute inset-0 rounded-full bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"></span>
                </button>


              </>
            ) : (
              <div className="px-3 py-1.5 bg-white text-blue-600 rounded-full shadow-md flex items-center gap-2 text-sm">
                <span>Select area</span>
                <button
                  onClick={cancelScreenshotSelection}
                  className="p-1 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 flex items-center"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>

          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-red-500 text-center p-4">
                <p className="text-lg font-semibold mb-2">Error Loading PDF</p>
                <p>{error}</p>
              </div>
            </div>
          )}

          {/* Selection overlay */}
          {isSelectingArea && isDragging && (
            <div
              ref={selectionRef}
              className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 z-10 pointer-events-none"
              style={{
                left: Math.min(selectionStart.x, selectionEnd.x) + "px",
                top: Math.min(selectionStart.y, selectionEnd.y) + "px",
                width: Math.abs(selectionEnd.x - selectionStart.x) + "px",
                height: Math.abs(selectionEnd.y - selectionStart.y) + "px",
              }}
            ></div>
          )}
          <div className="absolute bottom-2 left-2 z-10 flex space-x-2">

            <button
              onClick={() => window.open(proxiedUrl, "_blank")}
              disabled={isLoading}
              className="px-3 py-1.5 bg-blue-500 text-white rounded-full hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-md flex items-center gap-2 text-sm transition-all"
            >
              <Download size={16} />
              <span>Download PDF File</span>
            </button>
          </div>
          <div className="absolute w-full h-full">

          {/* Use your existing PDF viewer component */}
          <PdfViewer file={proxiedUrl} onError={handlePdfError} />
          </div>

        </div>

        {/* Right side - Chat */}
        <div className={`relative transition-all duration-300 ${chatExpanded ? "w-1/2" : "w-1/3"}`}>
          {/* Chat resize button */}
          <button
            onClick={toggleChatSize}
            className="absolute top-2 left-2 z-10 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-all"
            title={chatExpanded ? "Shrink chat" : "Expand chat"}
          >
            {chatExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <div className="absolute w-full h-full">

            <ChatBox
              initialContext={selectedScreenshot ? "I have a question about this PDF screenshot." : ""}
              screenshot={selectedScreenshot}
            />
          </div>

        </div>
      </div>
    </div>
  )
}

export default PdfViewerWithChat

